// useDeviceClass — ONE answer to "is this a phone?" (farm audit X-11, spec W-16).
//
// Components used to decide on one signal each, and the two signals disagree
// on a tablet: SearchableSelect keyed on `pointer: coarse` alone, so a 1024
// iPad got a phone's full-screen sheet for every picker (SP-07); the Records
// filter bar keyed on width alone, so the same iPad got a desktop's always-open
// filter block (SP-02). A touch tablet is neither a phone nor a desktop.
//
// What it decides: a phone sheet vs a tablet popover, a disclosure vs a pinned
// filter bar, a thumb button.
//
//   desktop — the primary pointer is NOT coarse: a mouse or trackpad, whatever
//             the size. A touch laptop with a trackpad is a desktop.
//   phone   — coarse AND phone-sized: the device's SHORT side, or the window's
//             width, is under 600 CSS px.
//   tablet  — coarse and bigger than that.
//
// Why the device's short side and not the window's width alone: a phone on its
// side is 844 px wide and is still a phone (its window is 390 px tall — no room
// for a list anchored under a field). Why not the window's height: an Android keyboard
// shrinks the window (and W-12 asks it to), so a landscape tablet would turn
// into a "phone" the moment someone typed. `screen` does neither — it is the
// device, in either orientation. The window's WIDTH still counts, so a tablet
// app squeezed into a narrow split-screen column is treated as the phone it
// now looks like; a keyboard never changes the width.
//
// Why 600: it is Android's own phone/tablet line (the `sw600dp` resource
// qualifier — Chrome's CSS px are Android's dp), and it sits in an empty band:
// the biggest phones are 412–440 px on their short side (Galaxy Ultra 412,
// iPhone Pro Max 430–440), the smallest tablets 600 (7" Android) and 744 (iPad
// mini).
//
// This answers "what kind of device is this" — NOT "how much room does this
// layout have". Widths stay with `sm:`/`md:` and, inside a dialog, the `cq-*:`
// container variants. And `useDeviceClass() !== 'desktop'` is exactly the
// Tailwind `coarse:` variant (both read `(pointer: coarse)`), so a CSS-only
// "touch" style and this hook can never disagree about which side a device is on.
//
// Chrome 86 (the floor's Blackview handsets): matchMedia, `screen` and
// useSyncExternalStore are all older than it; MediaQueryList.addEventListener
// is not in Safari < 14, so the subscription falls back to addListener.

import { useSyncExternalStore } from 'react'

export type DeviceClass = 'phone' | 'tablet' | 'desktop'

/** Under this many CSS px (device short side, or window width) a touch screen is a phone. */
export const PHONE_MAX_SIDE = 600

export interface DeviceSignals {
  /** `(pointer: coarse)` — the primary pointer is a finger. */
  coarse:  boolean
  /** `screen.width` / `screen.height`, CSS px, either orientation; 0 when unknown. */
  screenW: number
  screenH: number
  /** `window.innerWidth`, CSS px; 0 when unknown. */
  windowW: number
}

/** The rule, pure — see the header for why each signal is (and is not) in it. */
export function deviceClassOf({ coarse, screenW, screenH, windowW }: DeviceSignals): DeviceClass {
  if (!coarse) return 'desktop'
  const sides = [screenW, screenH, windowW].filter(n => n > 0)
  const smallest = sides.length ? Math.min(...sides) : Infinity
  return smallest < PHONE_MAX_SIDE ? 'phone' : 'tablet'
}

const COARSE = '(pointer: coarse)'

/** The live signals; 'desktop' wherever there is no window or no matchMedia (SSR, jsdom). */
export function readDeviceClass(): DeviceClass {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'desktop'
  const s = window.screen
  return deviceClassOf({
    coarse:  window.matchMedia(COARSE).matches,
    screenW: s?.width ?? 0,
    screenH: s?.height ?? 0,
    windowW: window.innerWidth ?? 0,
  })
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  // resize also fires on rotation, a fold, and a split-screen drag.
  window.addEventListener('resize', onChange)
  const mql = typeof window.matchMedia === 'function' ? window.matchMedia(COARSE) : null
  // A detachable keyboard (Surface, iPad Magic Keyboard) flips the pointer.
  if (mql?.addEventListener) mql.addEventListener('change', onChange)
  else mql?.addListener?.(onChange)
  return () => {
    window.removeEventListener('resize', onChange)
    if (mql?.removeEventListener) mql.removeEventListener('change', onChange)
    else mql?.removeListener?.(onChange)
  }
}

/** 'phone' | 'tablet' | 'desktop', kept current. Read synchronously on the
 *  first render (no phone-then-tablet flash); re-renders only when the class
 *  itself changes, not on every resize. */
export function useDeviceClass(): DeviceClass {
  return useSyncExternalStore(subscribe, readDeviceClass, () => 'desktop')
}
